import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchSupportPhoto,
  getSupportConversation,
  getSupportConversations,
  getSupportStats,
  SupportAgentStats,
  SupportConversationDetail,
  SupportConversationRow,
  SupportMessage,
  SupportQuote,
  SupportStats,
} from '../../api/services/supportChatService';
import { ActionRow, Avatar, Band, Card, ICONS, LineIcon, SectionLabel, StatusPill } from '../../components/ui/dg';

/**
 * Historique des discussions « Contacter le support » de l'app.
 *
 * Lecture seule, volontairement : on répond depuis Telegram, là où l'équipe
 * est déjà. Ici on retrouve qui a écrit, qui a pris la discussion, ce qui a
 * été dit — pour un litige, une relecture, ou voir ce qui attend encore une
 * réponse.
 */

type StatusFilter = 'all' | 'open' | 'closed';

const CLOSED_BY_LABELS: Record<string, string> = {
  user: 'Terminée par le client',
  admin: 'Terminée par l’équipe',
  timeout: 'Expirée (4 h sans message)',
};

const formatDateTime = (dateString?: string | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatTime = (dateString?: string | null) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const shortId = (id: string) => id.slice(0, 6).toUpperCase();

const LANG_FLAGS: Record<string, string> = { fr: '🇫🇷', ar: '🇹🇳', en: '🇬🇧' };

const formatDuration = (seconds: number | null) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3600).toFixed(1)} h`;
};

const satisfaction = (row: SupportAgentStats) => {
  const total = row.rated_up + row.rated_down;
  return total === 0 ? null : Math.round((100 * row.rated_up) / total);
};


const quoteAuthor = (quote: SupportQuote) =>
  quote.sender === 'user' ? 'Client' : quote.author_name ? `${quote.author_name} de Dingo` : 'Support Dingo';

const quoteExcerpt = (quote: SupportQuote) =>
  quote.body ? (quote.has_image ? `📷 ${quote.body}` : quote.body) : quote.has_image ? '📷 Photo' : '';

/** Le message cité au-dessus d'une bulle, comme sur WhatsApp. */
const QuoteBlock = ({ quote, onDark }: { quote: SupportQuote; onDark: boolean }) => (
  <div className={`mb-1.5 flex rounded-lg overflow-hidden ${onDark ? 'bg-white/15' : 'bg-black/5'}`}>
    <div className={`w-[3px] shrink-0 ${onDark ? 'bg-white' : 'bg-indigo-500'}`} />
    <div className="px-2 py-1 min-w-0">
      <p className={`text-[11px] font-semibold ${onDark ? 'text-white' : 'text-indigo-600'}`}>{quoteAuthor(quote)}</p>
      <p className={`text-[12px] truncate ${onDark ? 'text-white/85' : 'text-gray-600'}`}>{quoteExcerpt(quote)}</p>
    </div>
  </div>
);

/** Photo du fil, chargée avec le jeton ; un clic l'ouvre en grand dans un nouvel onglet. */
const ChatPhoto = ({ url }: { url: string }) => {
  const { token } = useAuth();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!token) return;
    let objectUrl: string | null = null;
    fetchSupportPhoto(url, token)
      .then((u) => { objectUrl = u; setSrc(u); })
      .catch(() => setFailed(true));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url, token]);
  if (failed) return <p className="text-xs text-gray-400 italic">Photo indisponible</p>;
  if (!src) return <div className="h-40 w-52 animate-pulse rounded-lg bg-gray-200" />;
  return (
    <a href={src} target="_blank" rel="noreferrer">
      <img src={src} alt="Photo envoyée par le client" className="h-40 w-52 rounded-lg object-cover" />
    </a>
  );
};

/** ✓ remis, ✓✓ lu — sur les messages de l'équipe, le point de vue du client. */
const Ticks = ({ read }: { read: boolean }) => (
  <span className={`ml-1 text-[11px] leading-none ${read ? 'text-sky-200' : 'text-white/70'}`} title={read ? 'Lu par le client' : 'Remis'}>
    {read ? '✓✓' : '✓'}
  </span>
);

/** Un message du fil. Le client à gauche, l'équipe à droite, les événements au milieu. */
const MessageBubble = ({ message }: { message: SupportMessage }) => {
  if (message.sender === 'system') {
    const text = message.event === 'assigned' && message.author_name
      ? `${message.author_name} de Dingo a pris en charge la demande`
      : message.event === 'closed' && message.author_name
        ? `${message.author_name} de Dingo a terminé la discussion`
        : message.event === 'reminder'
          ? `⏰ ${message.body}`
          : message.body;
    return (
      <div className="flex justify-center">
        <p className="text-[11.5px] text-gray-500 text-center px-3 py-1">
          {text} · {formatTime(message.created_at)}
        </p>
      </div>
    );
  }
  const fromTeam = message.sender === 'admin';
  return (
    <div className={`flex ${fromTeam ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[62%] ${fromTeam ? 'items-end' : 'items-start'} flex flex-col`}>
        {fromTeam && message.author_name && (
          <p className="text-[11px] font-semibold text-gray-500 mb-0.5 px-1">{message.author_name} de Dingo</p>
        )}
        <div
          className={`px-3.5 py-2.5 text-[13.5px] leading-[1.45] whitespace-pre-wrap break-words ${
            fromTeam
              ? 'bg-[#4F46E5] text-white rounded-[18px] rounded-br-[4px]'
              : 'bg-[#F1F2F8] text-[#1E293B] rounded-[18px] rounded-bl-[4px]'
          }`}
        >
          {message.reply_to && <QuoteBlock quote={message.reply_to} onDark={fromTeam} />}
          {message.image_url && (
            <div className={message.body ? 'mb-1.5' : ''}>
              <ChatPhoto url={message.image_url} />
            </div>
          )}
          {message.body}
          <span className={`block text-right text-[10.5px] mt-1 ${fromTeam ? 'text-white/70' : 'text-[#9CA3AF]'}`}>
            {formatTime(message.created_at)}
            {fromTeam && <Ticks read={Boolean(message.read_at)} />}
          </span>
        </div>
      </div>
    </div>
  );
};

const SupportConversations = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState<SupportConversationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SupportConversationDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [stats, setStats] = useState<SupportStats | null>(null);

  const load = useCallback((silent = false) => {
    if (!token) return;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    getSupportConversations(token, 'all')
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((err: any) => { if (!silent) setError(err.message || 'Erreur lors du chargement des discussions'); })
      .finally(() => { if (!silent) setLoading(false); });
    getSupportStats(token).then(setStats).catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Les discussions vivent en ce moment même : la liste se rafraîchit
  // discrètement, sans squelette, tant que l'onglet est affiché.
  useEffect(() => {
    const timer = setInterval(() => load(true), 20000);
    return () => clearInterval(timer);
  }, [load]);

  // Le fil ouvert suit la discussion en direct tant qu'elle est en cours.
  useEffect(() => {
    if (!selectedId || !token) return;
    let cancelled = false;
    const fetchDetail = () => {
      getSupportConversation(selectedId, token)
        .then((data) => { if (!cancelled) { setDetail(data); setDetailError(null); } })
        .catch((err: any) => { if (!cancelled) setDetailError(err.message || 'Discussion introuvable'); });
    };
    fetchDetail();
    const timer = setInterval(() => {
      if (detail?.conversation.status === 'closed') return;
      fetchDetail();
    }, 5000);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, token]);

  const filtered = useMemo(
    () => rows.filter((r) => statusFilter === 'all' || r.status === statusFilter),
    [rows, statusFilter]
  );

  const counts = useMemo(() => ({
    open: rows.filter((r) => r.status === 'open').length,
    waiting: rows.filter((r) => r.status === 'open' && r.admin_message_count === 0).length,
    closed: rows.filter((r) => r.status === 'closed').length,
  }), [rows]);


  // Une discussion est toujours ouverte au milieu : la première sans réponse à défaut.
  useEffect(() => {
    if (loading || selectedId || filtered.length === 0) return;
    const firstWaiting = filtered.find((r) => r.status === 'open' && r.admin_message_count === 0) || filtered[0];
    setSelectedId(firstWaiting.id_conversation);
  }, [loading, filtered, selectedId]);

  const c = detail?.conversation;
  const name = (r: { user_first_name: string; user_last_name: string }) => `${r.user_first_name || ''} ${r.user_last_name || ''}`.trim() || 'Client';
  const ago = (iso: string) => {
    const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return 'à l’instant';
    if (m < 60) return `${m} min`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} h`;
    return Math.round(h / 24) === 1 ? 'hier' : new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
      {/* Les discussions */}
      <Card className="flex w-[330px] shrink-0 flex-col overflow-hidden px-2 py-2.5">
        <div className="flex items-center justify-between px-3 pb-2 pt-2">
          <SectionLabel>{counts.waiting > 0 ? `${counts.waiting} sans réponse` : `${filtered.length} discussions`}</SectionLabel>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)} aria-label="Afficher"
            className="rounded-full border border-[#E5E7EB] bg-white px-2 py-1 text-[12px] text-[#4B5563] focus:outline-none">
            <option value="all">Toutes</option>
            <option value="open">En cours</option>
            <option value="closed">Terminées</option>
          </select>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {error && <p className="px-3 py-2 text-[12.5px] text-[#B91C1C]">{error}</p>}
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-3 px-3.5 py-3"><div className="h-[38px] w-[38px] animate-pulse rounded-full bg-[#EEF0F5]" /><div className="flex-1 space-y-2"><div className="h-3.5 w-28 animate-pulse rounded bg-[#EEF0F5]" /><div className="h-3 w-40 animate-pulse rounded bg-[#EEF0F5]" /></div></div>
            ))
          ) : filtered.length === 0 ? (
            <p className="px-4 py-10 text-center text-[13px] text-[#6B7280]">Aucune discussion pour l’instant.</p>
          ) : filtered.map((r) => {
            const selected = r.id_conversation === selectedId;
            const unanswered = r.status === 'open' && r.admin_message_count === 0;
            return (
              <button key={r.id_conversation} type="button" onClick={() => { setSelectedId(r.id_conversation); setDetail(null); }}
                className={`flex w-full gap-3 rounded-[14px] px-3.5 py-3 text-left transition-colors ${selected ? 'bg-[#F7F6FF] shadow-[inset_0_0_0_1.5px_#C7C4F6]' : 'hover:bg-[#FBFBFE]'}`}>
                <Avatar name={name(r)} size={38} tone={selected ? 'indigo' : 'grey'} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <b className="truncate text-[14px] font-semibold text-[#1E293B]">{name(r)}{r.lang && LANG_FLAGS[r.lang] ? ` ${LANG_FLAGS[r.lang]}` : ''}</b>
                    <span className="shrink-0 text-[11.5px] text-[#9CA3AF]">{ago(r.last_message_at)}</span>
                  </span>
                  <span className={`block truncate text-[12px] ${unanswered ? 'font-semibold text-[#1E293B]' : 'text-[#6B7280]'}`}>
                    {r.last_message_body || (r.last_message_sender ? 'Photo' : '—')}
                  </span>
                  <span className="mt-1.5 flex flex-wrap gap-1">
                    {r.rental_id && <StatusPill tone="indigo">Location #{shortId(r.rental_id)}</StatusPill>}
                    {r.status === 'closed' && <StatusPill tone="grey">Terminée</StatusPill>}
                    {unanswered && <StatusPill tone="amber">Sans réponse</StatusPill>}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Le fil */}
      <Card className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex items-center gap-3 border-b border-[#EEF0F4] px-5 py-3.5">
          {c ? (
            <>
              <Avatar name={name(c)} size={40} />
              <div className="min-w-0 flex-1">
                <b className="block truncate text-[15px] font-extrabold text-[#1E293B]">{name(c)}</b>
                <span className={`block text-[12px] font-semibold ${c.status === 'open' ? 'text-[#047857]' : 'text-[#6B7280]'}`}>
                  {c.status === 'open' ? (c.agent_name ? `En cours · ${c.agent_name} de Dingo` : 'En cours · personne encore') : (CLOSED_BY_LABELS[c.closed_by || ''] || 'Terminée')}
                </span>
              </div>
              {c.rating && <StatusPill tone={c.rating === 'up' ? 'green' : 'red'}>{c.rating === 'up' ? 'Résolu' : 'Non résolu'}</StatusPill>}
            </>
          ) : <div className="h-10 w-48 animate-pulse rounded-lg bg-[#EEF0F5]" />}
        </div>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-[#FBFBFE] px-5 py-4">
          {detailError && <p className="text-[13px] text-[#B91C1C]">{detailError}</p>}
          {!detail && !detailError && selectedId && (
            <div className="space-y-3"><div className="h-10 w-2/3 animate-pulse rounded-2xl bg-[#EEF0F5]" /><div className="ml-auto h-10 w-1/2 animate-pulse rounded-2xl bg-[#EEF0F5]" /></div>
          )}
          {detail?.conversation && (
            <>
              <p className="pb-1 text-center text-[11.5px] text-[#9CA3AF]">Ouverte le {formatDateTime(detail.conversation.created_at)}</p>
              {detail.messages.map((m) => <MessageBubble key={m.id_message} message={m} />)}
            </>
          )}
        </div>
        {/* On répond depuis Telegram : ici, on relit. */}
        <div className="border-t border-[#EEF0F4] px-4 py-3">
          <div className="flex h-[46px] items-center gap-2.5 rounded-full bg-[#F1F2F8] px-[18px] text-[13px] text-[#6B7280]">
            <LineIcon d={ICONS.message} size={16} color="#9CA3AF" />
            {c?.status === 'closed' ? 'Discussion terminée.' : 'Pour répondre, faites « Répondre » sur le message du client dans Telegram.'}
          </div>
        </div>
      </Card>

      {/* Le contexte */}
      <Card className="hidden w-[330px] shrink-0 overflow-y-auto xl:block">
        <div className="px-5 pb-1.5 pt-4"><SectionLabel>La personne</SectionLabel></div>
        {c ? (
          <div className="flex items-center gap-3 px-5 py-2.5">
            <Avatar name={name(c)} size={40} />
            <div className="min-w-0">
              <b className="block truncate text-[14px] font-semibold text-[#1E293B]">{name(c)}</b>
              <span className="block truncate text-[12px] text-[#6B7280]">{c.user_phone || c.user_email || '—'}</span>
            </div>
          </div>
        ) : <div className="mx-5 my-2 h-10 animate-pulse rounded-lg bg-[#EEF0F5]" />}
        {c?.rental_id && (
          <>
            <Band className="mt-2" />
            <div className="px-5 pb-1.5 pt-4"><SectionLabel>Location concernée</SectionLabel></div>
            <ActionRow first inset={20} icon="car" title={`Location #${shortId(c.rental_id)}`} subtitle="Ouvrir dans les trajets"
              onClick={() => { window.location.href = `/mytrips?q=${encodeURIComponent(c.rental_id!.slice(0, 8))}`; }} />
          </>
        )}
        {c && (c.user_phone || c.user_email) && (
          <>
            <Band className="mt-2" />
            <div className="px-5 pb-1.5 pt-4"><SectionLabel>Actions</SectionLabel></div>
            {c.user_phone && <ActionRow first inset={20} icon="phone" title="Appeler" subtitle={c.user_phone} onClick={() => { window.location.href = `tel:${c.user_phone!.replace(/[^\d+]/g, '')}`; }} />}
            {c.user_email && <ActionRow first={!c.user_phone} inset={20} icon="mail" tone="grey" title="Écrire un e-mail" subtitle={c.user_email} onClick={() => { window.location.href = `mailto:${c.user_email}`; }} />}
          </>
        )}
        <Band className="mt-2" />
        <div className="px-5 pb-1.5 pt-4"><SectionLabel>Le support</SectionLabel></div>
        <div className="px-5 pb-4 text-[13px]">
          {[
            ['En cours', String(counts.open)],
            ['Sans réponse', String(counts.waiting)],
            ['Première réponse (moy.)', formatDuration(stats?.total?.avg_first_response_seconds ?? null)],
            ['Satisfaction', stats?.total && satisfaction(stats.total) != null ? `${satisfaction(stats.total)} %` : '—'],
          ].map(([a, b], i) => (
            <div key={a} className="flex py-2" style={{ borderTop: i ? '1px solid #EEF0F4' : 'none' }}>
              <span className="flex-1 text-[#6B7280]">{a}</span><b className="font-bold text-[#1E293B]">{b}</b>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default SupportConversations;
